import { useState } from "react";
import { ExternalLink, Play } from "lucide-react";
import { exerciseVisual } from "@/lib/exerciseVisuals";
import { exerciseMuscles } from "@/lib/exerciseMuscles";
import MuscleAnatomyDiagram from "@/components/MuscleAnatomyDiagram";

// Renders a single exercise (e.g. "Incline DB Press 4x8") with:
//   • a thumbnail GIF (from free-exercise-db jsDelivr CDN)
//   • a WATCH FORM link to YouTube tutorial (untouched per product brief)
//   • an automatically-shown front/back muscle anatomy diagram with
//     primary muscles in red and secondary muscles in amber. No extra tap
//     required — the diagram is visible by default so the user instantly
//     sees what they're training.

export default function ExerciseLine({ text, testId }) {
  const visual = exerciseVisual(text);
  const muscles = exerciseMuscles(text);
  const [imgErr, setImgErr] = useState(false);
  const hasImg = visual?.image_url && !imgErr;
  const hasMuscles = (muscles.primary?.length || 0) + (muscles.secondary?.length || 0) > 0;

  return (
    <div
      data-testid={testId}
      style={{
        padding: "10px 4px",
        borderBottom: "1px dashed rgba(255,255,255,0.06)",
      }}
    >
      {/* Top row — thumbnail + name + watch form (unchanged) */}
      <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
        <div
          style={{
            width: 56, height: 56, flexShrink: 0,
            borderRadius: 10, overflow: "hidden",
            background: "var(--surface-soft)",
            border: "1px solid var(--line)",
            display: "flex", alignItems: "center", justifyContent: "center",
            position: "relative",
          }}
        >
          {hasImg ? (
            <img
              src={visual.image_url}
              alt=""
              loading="lazy"
              onError={() => setImgErr(true)}
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
              data-testid={testId ? `${testId}-img` : undefined}
            />
          ) : (
            <Play size={16} color="var(--gold)" strokeWidth={1.8} />
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="body-sm" style={{ color: "var(--text)", lineHeight: 1.4 }}>{text}</div>
          <a
            href={visual.youtube_url}
            target="_blank"
            rel="noopener noreferrer"
            className="tiny"
            style={{
              display: "inline-flex", alignItems: "center", gap: 4,
              marginTop: 2, color: "var(--gold)",
              letterSpacing: "0.12em", textTransform: "uppercase",
              textDecoration: "none", fontWeight: 600,
            }}
            data-testid={testId ? `${testId}-watch` : undefined}
          >
            Watch form <ExternalLink size={10} strokeWidth={2} />
          </a>
        </div>
      </div>

      {/* Muscle diagram — auto-displayed below every exercise, no tap needed.
          Front + back silhouette with primary muscles in red, secondary in
          amber so the user understands exactly what's being trained. */}
      {hasMuscles && (
        <div style={{ marginTop: 10 }} data-testid={testId ? `${testId}-muscles` : undefined}>
          <MuscleAnatomyDiagram
            primary={muscles.primary}
            secondary={muscles.secondary}
            compact
            testId={testId ? `${testId}-muscle-diagram` : "exercise-muscle-diagram"}
          />
        </div>
      )}
    </div>
  );
}
